import { resortListNative } from 'util/resortList';

import { DiscreteQuestion } from 'model/DiscreteQuestion';
import { ImageWidget } from 'model/ImageWidget';
import { PageItemModel } from 'api/dto/PageItem.dto';
import { PageItemKind } from 'model/PageItemKind';
import { Question } from 'model/Question';
import { TextWidget } from 'model/TextWidget';
import { PageItemApi } from 'api/PageItemApi';
import { pageItemMapper } from 'container/pageItemMapper';

export type PageDto = {
  id: number;
  position?: number;
  items?: PageItemApi.Entry[];
};

export type PageModel = {
  id?: number;
  position?: number;
  items?: PageItemModel[];
};

export function mapPageModelToDto(model: PageModel): PageDto {
  return {
    id: model.id,
    position: model.position,
  };
}

export function mapPageDtoToModel(dto: PageDto): PageModel {
  return {
    id: dto.id,
    position: dto.position,
    items: (dto.items || [])
      .map((item) => pageItemMapper.deserialize(item))
      .sort((a, b) => a.position - b.position),
  };
}

export function mapPageDtoListToModel(dto: PageDto[]): PageModel[] {
  return (dto || [])
    .filter((dto) => !!dto)
    .map((dto) => mapPageDtoToModel(dto))
    .sort((a, b) => a.position - b.position);
}

export namespace PageModel {
  export function getQuestions(page: PageModel): Question[] {
    return page.items.filter(
      (item) =>
        PageItemModel.REGULAR_PROJECT_QUESTION_KINDS.includes(item.kind) ||
        PageItemModel.PULSE_PROJECT_QUESTION_KINDS.includes(item.kind),
    ) as Question[];
  }

  export function getDiscreteQuestions(page: PageModel): (Question & DiscreteQuestion)[] {
    return PageModel.getQuestions(page).filter(
      (question): question is Question & DiscreteQuestion =>
        question.kind !== PageItemKind.QuestionCommentsBox &&
        question.kind !== PageItemKind.QuestionForm,
    );
  }

  export function getMediaQuestions(page: PageModel): (ImageWidget | TextWidget)[] {
    return page.items.filter(
      (item) => item.kind === PageItemKind.ImageWidget || item.kind === PageItemKind.TextWidget,
    ) as (ImageWidget | TextWidget)[];
  }

  export function moveUp(page: PageModel): PageModel {
    return { ...page, position: page.position - 1 };
  }

  export function moveDown(page: PageModel): PageModel {
    return { ...page, position: page.position + 1 };
  }

  export function removeItem(page: PageModel, itemId: number): PageModel {
    return { ...page, items: page.items.filter((item) => item.id !== itemId) };
  }

  export function moveItem(page: PageModel, fromPosition: number, toPosition: number): PageModel {
    const items = resortListNative(page.items, fromPosition, toPosition).map((item, position) => ({
      ...item,
      position: position + 1,
    }));
    return { ...page, items };
  }

  export function insertItem(
    page: PageModel,
    toPosition: number,
    pageItem: PageItemModel,
  ): PageModel {
    const newItems = [
      ...page.items.slice(0, toPosition),
      pageItem,
      ...page.items.slice(toPosition),
    ].map((item, index) => ({ ...item, position: index + 1 }));
    return { ...page, items: newItems };
  }
}
